import { ChangeDetectionStrategy, Component, HostListener, signal } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import { DialogConfig, DialogRef } from '@stylehaven/components';

/**
 * What the user picked: re-send as files and metadata, drop the stukken without a digital representatie,
 * or stop. A dialog closed any other way (✕, Esc) closes with `undefined`, which the caller reads as `cancel`.
 */
export type AnalogExportChoice = 'filesAndMetadata' | 'remove' | 'cancel';

export interface AnalogExportDialogData {
    /** Stukken without a digital representatie — ticked ones and a ticked dossier's candidates alike. */
    readonly amount: number;
    /** False when dropping them would leave nothing to export; the choice is then not offered at all. */
    readonly canRemove: boolean;
}

/**
 * HFBD-171: asked before a files-only export of a selection holding stukken that exist only on paper.
 */
@Component({
    standalone: true,
    selector: 'digihaven-analog-export-dialog',
    templateUrl: './analog-export-dialog.component.html',
    styleUrls: ['./analog-export-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule]
})
export class AnalogExportDialogComponent {
    readonly choice = signal<Exclude<AnalogExportChoice, 'cancel'>>('filesAndMetadata');

    constructor(
        public readonly config: DialogConfig<AnalogExportDialogData>,
        public readonly dialog: DialogRef<AnalogExportChoice>
    ) {}

    get data(): AnalogExportDialogData {
        return this.config.data!;
    }

    /** Enter on a focused button is that button's own click — only elsewhere does it mean "Doorgaan". */
    @HostListener('document:keydown.enter', ['$event'])
    onEnter(event: KeyboardEvent): void {
        if (event.target instanceof HTMLButtonElement) {
            return;
        }

        this.proceed();
    }

    select(choice: Exclude<AnalogExportChoice, 'cancel'>): void {
        this.choice.set(choice);
    }

    /** With nothing to remove there is only one way forward, so the button itself is that choice. */
    proceed(): void {
        this.dialog.close(this.data.canRemove ? this.choice() : 'filesAndMetadata');
    }

    cancel(): void {
        this.dialog.close('cancel');
    }
}
